import { useEffect, useState, useMemo, useRef } from 'react';
import { api } from '../App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, Pencil, Trash, Eye, MagnifyingGlass, Printer, FileText, FileDoc } from '@phosphor-icons/react';
import { DateInput } from '../components/DateInput';
import { formatDatePrint } from '../utils/dateFormat';
import { printHTML, downloadWord } from '../utils/printHelper';
import { confirmDialog } from '@/components/ConfirmDialog';

const TIP_UGOVORA = [
  { value: 'racunovodstvo', label: 'Ugovor o računovodstvenim uslugama' },
  { value: 'zajam', label: 'Ugovor o zajmu' },
  { value: 'zakup', label: 'Ugovor o zakupu' },
  { value: 'djelo', label: 'Ugovor o djelu' },
  { value: 'saradnja', label: 'Ugovor o poslovnoj saradnji' },
  { value: 'ostalo', label: 'Ostali ugovori' },
];

const emptyForm = {
  client_id: '', tip_ugovora: 'racunovodstvo',
  zajmodavac_naziv: '', zajmodavac_identifikacija: '',
  zajmoprimac_naziv: '', zajmoprimac_identifikacija: '',
  iznos: '', iznos_slovima: '', kamata: '', rok_otplate: '', namjena: '',
  datum_ugovora: new Date().toISOString().split('T')[0], napomena: '',
  zakup_adresa: '', zakup_povrsina: '', zakup_kat_parcela: '', zakup_list_nepokretnosti: '',
  zakup_period_od: '', zakup_period_do: '', zakup_namjena: '', zakup_troskovi: '', zakup_grad: 'Ulcinj',
  djelo_grad: 'Ulcinj', djelo_radovi: '', djelo_izvrsilac_grad: '',
  djelo_narucilac_ovlasceno: '', djelo_izvrsilac_ovlasceno: '',
};

// Convert number to words in Montenegrin/Serbian
function brojSlovima(num) {
  if (!num && num !== 0) return '';
  const n = typeof num === 'string' ? parseFloat(num.replace(',', '.')) : num;
  if (isNaN(n)) return '';

  const jedinice = ['', 'jedan', 'dva', 'tri', 'četiri', 'pet', 'šest', 'sedam', 'osam', 'devet'];
  const teens = ['deset', 'jedanaest', 'dvanaest', 'trinaest', 'četrnaest', 'petnaest', 'šesnaest', 'sedamnaest', 'osamnaest', 'devetnaest'];
  const desetice = ['', '', 'dvadeset', 'trideset', 'četrdeset', 'pedeset', 'šezdeset', 'sedamdeset', 'osamdeset', 'devedeset'];
  const stotine = ['', 'sto', 'dvjesta', 'trista', 'četiristo', 'petsto', 'šesto', 'sedamsto', 'osamsto', 'devetsto'];

  const convertGroup = (n) => {
    if (n === 0) return '';
    let result = '';
    const s = Math.floor(n / 100);
    const d = Math.floor((n % 100) / 10);
    const j = n % 10;
    if (s > 0) result += stotine[s];
    if (d === 1) {
      result += teens[j];
      return result;
    }
    if (d > 1) result += desetice[d];
    if (j > 0) result += jedinice[j];
    return result;
  };

  const whole = Math.floor(Math.abs(n));
  const cents = Math.round((Math.abs(n) - whole) * 100);

  if (whole === 0 && cents === 0) return 'nula eura';

  let result = '';
  if (whole >= 1000000) {
    const mil = Math.floor(whole / 1000000);
    if (mil === 1) result += 'milion';
    else result += convertGroup(mil) + 'miliona';
  }
  const thousands = Math.floor((whole % 1000000) / 1000);
  if (thousands > 0) {
    if (thousands === 1) result += 'hiljadu';
    else if (thousands >= 2 && thousands <= 4) result += convertGroup(thousands) + 'hiljade';
    else result += convertGroup(thousands) + 'hiljada';
  }
  const remainder = whole % 1000;
  if (remainder > 0) result += convertGroup(remainder);

  if (whole === 0) result = 'nula';
  result += 'eura';
  if (cents > 0) result += convertGroup(cents) + 'centi';

  return result;
}

function generateUgovorZajam(u, client) {
  const datum = formatDatePrint(u.datum_ugovora) || '__.__.____';
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Ugovor o zajmu</title>
<style>
@page{size:A4;margin:14mm 18mm}
*{margin:0;padding:0;box-sizing:border-box}
body{font-family:'Times New Roman',Times,serif;font-size:11pt;color:#000;line-height:1.3}
p{text-align:justify;margin:3px 0}
.title{text-align:center;font-weight:bold;font-size:13pt;margin:6px 0 8px;text-transform:uppercase}
.clan{font-weight:bold;text-align:center;margin:5px 0 2px;font-size:11pt}
.page-break{page-break-before:always}
.signatures{display:flex;justify-content:space-between;margin-top:40px;gap:30px}
.sig-block{text-align:center;flex:1;font-weight:bold;font-size:11pt}
.sig-line{border-top:1px solid #000;width:100%;padding-top:4px;margin-top:50px;font-weight:normal}
@media print{body{margin:0}}
</style></head><body>

<p>Na osnovu člana 566 do 574 Zakona o obligacionim odnosima Crne Gore (Službeni list Crne Gore broj 47/08 i 4/11) ${u.zajmoprimac_naziv}, ${u.zajmoprimac_identifikacija ? 'PIB ' + u.zajmoprimac_identifikacija : ''} ${client ? ', koje zastupa izvršni direktor ' + (client.direktor || '') : ''} (u daljem tekstu: Zajmoprimac):</p>
<p>i</p>
<p>${u.zajmodavac_naziv} ${u.zajmodavac_identifikacija ? (u.zajmodavac_identifikacija.length === 13 ? 'JMBG ' + u.zajmodavac_identifikacija : 'broj pasoša ' + u.zajmodavac_identifikacija) : ''} (u daljem tekstu: Zajmodavac),</p>
<p>dana ${datum} godine zaključili su:</p>

<div class="title">UGOVOR O ZAJMU</div>

<div class="clan">Član 1</div>
<p>Zajmodavac daje a Zajmoprimac prima na zajam novčana sredstva do iznosa od ${u.iznos} € ${u.iznos_slovima ? '(Slovima: ' + u.iznos_slovima + ')' : ''}.</p>
<p>Uplata zajma vršiće se u više rata, u zavisnosti od potrebe Zajmoprimca, sve dok se zajam ne realizuje u ukupnom iznosu.</p>

<div class="clan">Član 2</div>
<p>Zajam će se koristiti isključivo za ${u.namjena || 'plaćanje tekućih obaveza Zajmoprimca'}.</p>

<div class="clan">Član 3</div>
<p>Na zajam će se obračunati kamata u iznosu od ${u.kamata || '2'}% godišnje.</p>
<p>Kamata se obračunava na zbirni iznos iskorišćenog zajma u protekloj poslovnoj godini, za svaku godinu pojedinačno.</p>
<p>Kamata se pripisuje zajmu.</p>

<div class="clan">Član 4</div>
<p>Zajam se odobrava na rok otplate od ${u.rok_otplate || '5'} godina.</p>
<p>Rok za vraćanje zajma počinje teći od kraja poslovne godine u kojoj je zajam iskorišćen u cjelosti.</p>
<p>Povraćaj iskorišćenog zajma sa pripisanom kamatom vršiće se jednokratno ili u više polugodišnjih anuiteta, u zavisnosti od raspoloživih sredstava Zajmoprimca.</p>

<div class="clan">Član 5</div>
<p>Za sve što nije regulisano ovim ugovorom važe odredbe Zakona o obligacionim odnosima Crne Gore.</p>

<div class="signatures">
<div class="sig-block">ZAJMODAVAC<div class="sig-line">${u.zajmodavac_naziv || '_______________'}</div></div>
<div class="sig-block">ZAJMOPRIMAC<div class="sig-line">${(client && client.direktor) || '_______________'}</div></div>
</div>

</body></html>`;
}

function generateUgovorZakup(u, client) {
  const datum = formatDatePrint(u.datum_ugovora) || '__.__.____';
  const periodOd = formatDatePrint(u.zakup_period_od) || '__.__.____';
  const periodDo = formatDatePrint(u.zakup_period_do) || '__.__.____';
  const direktor = client?.direktor || '';
  const grad = u.zakup_grad || 'Ulcinj';

  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Ugovor o zakupu</title>
<style>
@page{size:A4;margin:20mm 25mm}
*{margin:0;padding:0;box-sizing:border-box}
body{font-family:Arial,Helvetica,sans-serif;font-size:12pt;color:#000;line-height:1.6}
p{text-align:justify;margin:6px 0}
.title{text-align:center;font-weight:bold;font-size:16pt;margin:20px 0 16px;text-transform:uppercase}
.section-title{font-weight:bold;text-align:center;margin:14px 0 6px;font-size:12pt}
.clan{font-weight:bold;text-align:center;margin:14px 0 4px}
.signatures{display:flex;justify-content:space-between;margin-top:50px}
.sig-block{text-align:center}
.sig-line{border-top:1px solid #000;min-width:180px;padding-top:4px;margin-top:40px}
.sig-sub{font-size:10pt;margin-top:2px}
@media print{body{margin:0}}
</style></head><body>

<div class="title">UGOVOR O ZAKUPU</div>

<p>Zaključen dana ${datum} godine u ${grad}u između ${u.zajmoprimac_naziv}, PIB: ${u.zajmoprimac_identifikacija || ''} koju zastupa ${direktor}, kao zakupoprimac s jedne strane i ${u.zajmodavac_naziv}${u.zajmodavac_identifikacija ? (u.zajmodavac_identifikacija.length === 13 ? ' iz ' + grad + 'a, JMBG: ' + u.zajmodavac_identifikacija : ', PIB: ' + u.zajmodavac_identifikacija) : ''} kao zakupodavac s druge strane.</p>

<div class="section-title">PREDMET UGOVORA</div>

<div class="clan">Član 1.</div>
<p>Ovim ugovorom zakupodavac izdaje u zakup zakupoprimcu objekat koji se nalazi u ${grad}u, ${u.zakup_adresa || '________________'} površine ${u.zakup_povrsina || '______'}${u.zakup_kat_parcela ? ', kat.parcela ' + u.zakup_kat_parcela : ''}${u.zakup_list_nepokretnosti ? ', upisanom u list nepokretnosti broj ' + u.zakup_list_nepokretnosti : ''}.</p>

<div class="clan">Član 2.</div>
<p>Objekat se izdaje u zakup za vremenski period na određeno vrijeme, a počinje ${periodOd} god do ${periodDo} god, i može da se produži ukoliko su obije strane saglasne, a može da se raskine po dogovoru.</p>

<div class="clan">Član 3.</div>
<p>Zakupnina je utvrđena u iznosu od ${u.iznos || '________'} € ${u.iznos_slovima ? '(slovima: ' + u.iznos_slovima + ')' : ''}, je iznos koji zakupoprimac isplaćuje na godišnjem nivou.</p>

<div class="clan">Član 4.</div>
<p>Saglasne su ugovorene strane da se prostor koristi za ${u.zakup_namjena || 'potrebe skladištenja robe u magacinu'}.</p>

<div class="clan">Član 5.</div>
<p>Zakupoprimac je dužan da zakupljeni prostor uredno održava i po isteku ugovorenog roka preda u urednom stanju. Za eventualne nedostatke zakupac je dužan da ih nadoknadi.</p>

<div class="clan">Član 6.</div>
<p>${u.zakup_troskovi || 'Zakupodavac je obavezan plaćati troškove struje i vode'}.</p>

<div class="clan">Član 7.</div>
<p>Za sve eventualne sporove iz ovog ugovora nadležan je Osnovni sud u ${grad}u.</p>

<div class="clan">Član 8.</div>
<p>U znak saglasnosti stranke svojeručno potpisuju ovaj Ugovor.</p>

<div class="signatures">
<div class="sig-block">ZAKUPODAVAC:<div class="sig-line">${u.zajmodavac_naziv}</div>${u.zajmodavac_identifikacija ? '<div class="sig-sub">JMB: ' + u.zajmodavac_identifikacija + '</div>' : ''}</div>
<div class="sig-block">ZAKUPOPRIMAC:<div class="sig-line">${u.zajmoprimac_naziv}</div>${u.zajmoprimac_identifikacija ? '<div class="sig-sub">PIB: ' + u.zajmoprimac_identifikacija + '</div>' : ''}</div>
</div>

</body></html>`;
}


function generateUgovorDjelo(u, client) {
  const datum = formatDatePrint(u.datum_ugovora) || '__.__.____';
  const grad = u.djelo_grad || 'Ulcinj';
  const izvGrad = u.djelo_izvrsilac_grad || grad;
  const narucilacNaziv = u.zajmoprimac_naziv || '________________';
  const narucilacPib = u.zajmoprimac_identifikacija || '';
  const narucilacAdresa = client?.adresa || '________________';
  const narucilacOvlasceno = u.djelo_narucilac_ovlasceno || (client?.direktor || '________________');
  const izvrsilacIme = u.zajmodavac_naziv || '________________';
  const izvrsilacJmbg = u.zajmodavac_identifikacija || '________________';
  const izvrsilacOvlasceno = u.djelo_izvrsilac_ovlasceno || izvrsilacIme;
  const radovi = u.djelo_radovi || '________________';
  const iznos = u.iznos || '________';
  const iznosSlovima = u.iznos_slovima || '________';

  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Ugovor o djelu</title>
<style>
@page{size:A4;margin:20mm 25mm}
*{margin:0;padding:0;box-sizing:border-box}
body{font-family:Arial,Helvetica,sans-serif;font-size:12pt;color:#000;line-height:1.6}
p{text-align:justify;margin:8px 0}
.title{text-align:center;font-weight:bold;font-size:18pt;margin:30px 0 20px;text-transform:uppercase;letter-spacing:1px}
.clan{font-weight:bold;text-align:center;margin:16px 0 4px}
.signatures{display:flex;justify-content:space-between;margin-top:60px}
.sig-block{text-align:center;width:45%}
.sig-title{font-weight:bold;margin-bottom:4px}
.sig-line{border-top:1px solid #000;min-width:180px;padding-top:4px;margin-top:50px}
.sig-sub{font-size:10pt;color:#555;margin-top:2px}
@media print{body{margin:0}}
</style></head><body>

<div class="title">UGOVOR<br>O DJELU</div>

<p>Zaključen u ${grad}, dana ${datum} godine, između:</p>
<p>Preduzeća <b>${narucilacNaziv}</b> iz ${grad}, ${narucilacAdresa}${narucilacPib ? ', PIB: ' + narucilacPib : ''}, koga predstavlja ${narucilacOvlasceno}, u daljem tekstu <b>"Naručilac"</b>, i</p>
<p><b>${izvrsilacIme}</b> iz ${izvGrad}, JMBG ${izvrsilacJmbg}, u daljem tekstu <b>"Izvršilac"</b>.</p>

<div class="clan">Član 1</div>
<p>Izvršilac je za Naručioca izveo radove: ${radovi}.</p>

<div class="clan">Član 2</div>
<p>Naručilac je za urađeni posao iz prethodnog člana Izvršiocu po završetku isplatio ukupan iznos od ${iznos} € (slovima: ${iznosSlovima}).</p>

<div class="clan">Član 3</div>
<p>Za sve što nije predviđeno ovim Ugovorom, primjenjuju se odredbe Zakona o obligacionim odnosima.</p>

<div class="clan">Član 4</div>
<p>U slučaju spora nadležan je sud u ${grad}.</p>

<div class="clan">Član 5</div>
<p>Ovaj Ugovor sačinjen je u 4 (četiri) istovjetna primjerka od kojih Naručilac zadržava 3 (tri) a Izvršilac 1 (jedan) primjerak.</p>

<div class="signatures">
<div class="sig-block"><div class="sig-title">Za naručioca</div><div class="sig-line">${narucilacOvlasceno}</div></div>
<div class="sig-block"><div class="sig-title">Izvršilac</div><div class="sig-line">${izvrsilacOvlasceno}</div>${izvrsilacJmbg && izvrsilacJmbg !== '________________' ? '<div class="sig-sub">JMBG: ' + izvrsilacJmbg + '</div>' : ''}</div>
</div>

</body></html>`;
}


function generateUgovorRacunovodstvo(u, client, agency) {
  const datum = formatDatePrint(u.datum_ugovora) || '__.__.____';
  const grad = (agency?.grad?.split(',')[0] || 'Ulcinj').trim();
  const agencijaNaziv = agency?.naziv || '________________';
  const agencijaPib = agency?.pib || '____________';
  const agencijaDirektor = agency?.potpisnik_ime || '________________';
  const agencijaJmbg = agency?.direktor_jmbg || '____________';
  const agencijaEmail = agency?.email || '_______________';
  const klijentNaziv = (client?.naziv_firme || u.zajmoprimac_naziv || '________________');
  const klijentPib = client?.pib || u.zajmoprimac_identifikacija || '____________';
  const klijentDirektor = client?.direktor || '________________';
  const klijentJmbg = client?.jmb_direktora || '____________';
  const klijentEmail = client?.email || '_______________';
  const iznos = u.iznos || '__________';

  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Ugovor o pružanju računovodstvenih usluga</title>
<style>
@page{size:A4;margin:14mm 18mm}
*{margin:0;padding:0;box-sizing:border-box}
body{font-family:'Times New Roman',Times,serif;font-size:11pt;color:#000;line-height:1.3}
p{text-align:justify;margin:3px 0}
.title{text-align:center;font-weight:bold;font-size:13pt;margin:6px 0 10px;text-transform:uppercase}
.section{text-align:center;font-weight:bold;text-transform:uppercase;margin:8px 0 3px;font-size:11pt}
.clan{text-align:center;font-weight:bold;margin:6px 0 3px;font-size:11pt}
.parties p{margin:2px 0}
ul{padding-left:22px;margin:3px 0}
ul li{margin:1px 0}
.signatures{display:flex;justify-content:space-between;margin-top:28px;gap:20px}
.sig-block{flex:1;text-align:center;font-weight:bold;font-size:11pt}
.sig-line{border-top:1px solid #000;min-width:200px;padding-top:4px;margin-top:35px;font-size:11pt;font-weight:normal}
@media print{body{margin:0}}
</style></head><body>

<div class="title">UGOVOR O PRUŽANJU RAČUNOVODSTVENIH USLUGA</div>

<div class="parties">
<p>Zaključen dana <b>${datum}</b> godine između:</p>
<p><b>${agencijaNaziv}</b>, koga zastupa: <b>${agencijaDirektor}</b>, PIB: <b>${agencijaPib}</b>, JMB/JMBG: <b>${agencijaJmbg}</b>, e-mail: <b>${agencijaEmail}</b> (u daljem tekstu: „Pružalac usluga")</p>
<p>i</p>
<p><b>${klijentNaziv}</b>, koga zastupa: <b>${klijentDirektor}</b>, PIB: <b>${klijentPib}</b>, JMB/JMBG: <b>${klijentJmbg}</b>, e-mail: <b>${klijentEmail}</b> (u daljem tekstu: „Korisnik usluga" ili „Klijent")</p>
</div>

<div class="section">PREDMET UGOVORA</div>

<div class="clan">Član 1</div>
<p>Predmet ovog ugovora je pružanje računovodstvenih usluga od strane Pružaoca usluga, u skladu sa Zakonom o računovodstvu Crne Gore i drugim važećim propisima koji uređuju finansijsko i poresko poslovanje u Crnoj Gori.</p>

<div class="clan">Član 2</div>
<p>Pružalac usluga će za Korisnika usluga obavljati sljedeće poslove:</p>
<ul>
<li>vođenje poslovnih knjiga;</li>
<li>obračun zarada i naknada;</li>
<li>pripremu i podnošenje poreskih prijava;</li>
<li>sastavljanje finansijskih izvještaja;</li>
<li>poresko i računovodstveno savjetovanje;</li>
<li>saradnju sa nadležnim organima;</li>
<li>druge računovodstvene usluge.</li>
</ul>

<div class="section">OBAVEZE PRUŽAOCA USLUGA</div>

<div class="clan">Član 3</div>
<p>Pružalac usluga se obavezuje da usluge obavlja stručno, savjesno i u skladu sa važećim propisima.</p>

<div class="section">OBAVEZE KORISNIKA USLUGA</div>

<div class="clan">Član 4</div>
<p>Korisnik usluga se obavezuje da:</p>
<ul>
<li>dostavlja tačnu i potpunu dokumentaciju najkasnije do 5. u mjesecu;</li>
<li>snosi odgovornost za tačnost dostavljenih podataka;</li>
<li>obavještava Pružaoca usluga o svim promjenama značajnim za poslovanje.</li>
</ul>

<div class="section">ODGOVORNOST</div>

<div class="clan">Član 5</div>
<p>Korisnik usluga snosi odgovornost za tačnost i blagovremenost dostavljene dokumentacije.</p>
<p>Pružalac usluga ne odgovara za posljedice nastale usljed zakašnjele ili netačne dokumentacije.</p>

<div class="section">POVJERLJIVOST</div>

<div class="clan">Član 6</div>
<p>Ugovorne strane se obavezuju da sve podatke i informacije čuvaju kao poslovnu tajnu.</p>

<div class="clan">Član 7</div>
<p>Korisnik usluga daje ovlašćenje Pružaocu usluga za korišćenje elektronskih servisa Poreske uprave Crne Gore putem IRMS sistema i drugih državnih elektronskih servisa radi izvršavanja ugovorenih računovodstvenih poslova. Ovlašćenje naročito obuhvata:</p>
<ul>
<li>podnošenje zahtjeva;</li>
<li>podnošenje i pregled poreskih prijava;</li>
<li>pregled analitičke kartice;</li>
<li>pristup e-Sandučetu;</li>
<li>pregled i preuzimanje elektronskih dokumenata;</li>
<li>elektronsku komunikaciju sa državnim organima.</li>
</ul>
<p>Pružalac usluga ne odgovara za tehničke probleme ili nedostupnost IRMS sistema.</p>

<div class="section">DOKUMENTACIJA</div>

<div class="clan">Član 8</div>
<p>Po završetku poslovne godine, Pružalac usluga će predati poslovnu dokumentaciju Korisniku usluga.</p>
<p>Korisnik usluga je obavezan da preuzetu poslovnu dokumentaciju čuva najmanje 5 godina, odnosno u rokovima propisanim važećim zakonskim propisima Crne Gore.</p>
<p>Nakon izvršene primopredaje dokumentacije, Pružalac usluga ne snosi odgovornost za dalje čuvanje, oštećenje ili gubitak iste.</p>

<div class="section">NAKNADA I PLAĆANJE</div>

<div class="clan">Član 9</div>
<p>Početna mjesečna naknada za pružanje računovodstvenih usluga iznosi <b>${iznos} EUR</b> bez PDV-a.</p>
<p>Plaćanje se vrši najkasnije do 15. u mjesecu za prethodni mjesec.</p>
<p>U slučaju proširenja djelatnosti Korisnika usluga, otvaranja novih poslovnih jedinica, objekata ili poslovnih prostora trajnog ili sezonskog karaktera, povećanja obima poslovanja, povećanja broja zaposlenih, povećanja obima dokumentacije, kao i usljed inflacije ili rasta troškova poslovanja, ugovorena cijena računovodstvenih usluga može biti uvećana po dogovoru ugovornih strana.</p>
<p>Izmjena cijene iz prethodnog stava reguliše se aneksom ovog ugovora.</p>

<div class="clan">Član 10</div>
<p>Korisnik usluga je obavezan da Pružaocu usluga dostavi aktivnu i važeću email adresu radi službene i svakodnevne poslovne komunikacije.</p>
<p>Elektronska komunikacija putem email-a smatra se zvaničnim načinom komunikacije između ugovornih strana, a sva obavještenja, upozorenja, izvještaji, poreske informacije, zahtjevi i druga dokumentacija smatraće se uredno dostavljenim danom slanja na posljednju dostavljenu email adresu Korisnika usluga.</p>
<p>Korisnik usluga je dužan da bez odlaganja obavijesti Pružaoca usluga o promjeni email adrese.</p>
<p>Dodatne usluge koje nijesu obuhvaćene redovnim računovodstvenim uslugama, uključujući ali ne ograničavajući se na: prijavu i odjavu radnika, registraciju promjena u CRPS-u, izradu posebnih izvještaja, poslovnih planova, zahtjeva, dopisa, analiza, dokumentacije za kredite, tendere, radne i boravišne dozvole, zastupanje pred državnim organima, kao i ostale administrativne i savjetodavne usluge, naplaćivaće se posebno u skladu sa važećim cjenovnikom Pružaoca usluga.</p>
<p>Korisnik usluga je saglasan da navedene dodatne usluge nijesu uključene u ugovorenu mjesečnu naknadu za računovodstvene usluge.</p>

<div class="section">RASKID UGOVORA</div>

<div class="clan">Član 11</div>
<p>Ugovor se zaključuje na neodređeno vrijeme.</p>
<p>Svaka ugovorna strana može raskinuti ugovor uz otkazni rok od 15 dana, dostavljanjem pisanog obavještenja drugoj ugovornoj strani.</p>
<p>Pružalac usluga ima pravo da jednostrano raskine ugovor bez posebnog prethodnog upozorenja ili opomene Korisniku usluga, ukoliko Korisnik usluga ne izvrši plaćanje ugovorenih usluga u periodu od dva uzastopna mjeseca.</p>
<p>U slučaju raskida ugovora iz prethodnog stava, Korisnik usluga je obavezan da izmiri sve dospjele obaveze nastale do dana raskida ugovora.</p>

<div class="section">ZAVRŠNE ODREDBE</div>

<div class="clan">Član 12</div>
<p>Za sve sporove po ovom ugovoru nadležan je Privredni sud u Podgorici.</p>

<div class="clan">Član 13</div>
<p>Ugovor je sačinjen u dva jednaka primjerka.</p>

<p style="margin-top:18px"><b>U ${grad}u, dana ${datum} godine.</b></p>

<div class="signatures">
  <div class="sig-block">PRUŽALAC USLUGA:<div class="sig-line">${agencijaDirektor}</div></div>
  <div class="sig-block">KORISNIK USLUGA:<div class="sig-line">${klijentDirektor}</div></div>
</div>

</body></html>`;
}


export default function UgovoriPage() {
  const [entries, setEntries] = useState([]);
  const [clients, setClients] = useState([]);
  const [agency, setAgency] = useState({});
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [showDetail, setShowDetail] = useState(null);
  const [search, setSearch] = useState('');
  const [filterClient, setFilterClient] = useState('all');
  const [filterTip, setFilterTip] = useState('all');
  const [flSearch, setFlSearch] = useState('');
  const [flResults, setFlResults] = useState([]);
  const [showFlDropdown, setShowFlDropdown] = useState(false);
  const flTimer = useRef(null);

  useEffect(() => { fetchAll(); }, []);
  useEffect(() => { fetchAll(); }, [filterClient, filterTip]);

  const fetchAll = async () => {
    try {
      const params = {};
      if (filterClient !== 'all') params.client_id = filterClient;
      if (filterTip !== 'all') params.tip = filterTip;
      const [e, c, s] = await Promise.all([
        api.get('/ugovori', { params }),
        api.get('/clients'),
        api.get('/settings').catch(() => ({ data: {} })),
      ]);
      setEntries(e.data);
      setClients(c.data);
      setAgency(s.data?.agency || {});
    } catch { toast.error('Greška'); }
    finally { setLoading(false); }
  };

  const sortedClients = useMemo(() =>
    [...clients].sort((a, b) => (a.skraceni_naziv || '').localeCompare(b.skraceni_naziv || '')), [clients]);

  const getClientName = (id) => { const c = clients.find(c => c.id === id); return c ? (c.skraceni_naziv || c.naziv_firme) : ''; };
  const getTipLabel = (v) => TIP_UGOVORA.find(t => t.value === v)?.label || v;

  const handleFlSearch = (val) => {
    setFlSearch(val);
    setForm(prev => ({ ...prev, zajmodavac_naziv: val }));
    setFlResults([]); setShowFlDropdown(false);
    clearTimeout(flTimer.current);
    if (val.length < 2) return;
    flTimer.current = setTimeout(async () => {
      try {
        const r = await api.get(`/fizicka-lica?search=${encodeURIComponent(val)}`);
        setFlResults(r.data.slice(0, 10));
        setShowFlDropdown(r.data.length > 0);
      } catch {}
    }, 400);
  };

  const handleFlSelect = (fl) => {
    const name = `${fl.prezime || ''} ${fl.ime || ''}`.trim();
    setForm(prev => ({
      ...prev,
      zajmodavac_naziv: name,
      zajmodavac_identifikacija: fl.jmb || fl.broj_dokumenta || '',
    }));
    setFlSearch(name);
    setFlResults([]); setShowFlDropdown(false);
    toast.success(`Izabrano: ${name}`);
  };

  const filtered = entries.filter(e => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (e.zajmodavac_naziv || '').toLowerCase().includes(s) || (e.zajmoprimac_naziv || '').toLowerCase().includes(s) || (e.iznos || '').includes(s);
  });

  const handleClientSelect = (cid) => {
    const c = clients.find(cl => cl.id === cid);
    if (c) {
      setForm(prev => ({
        ...prev,
        client_id: cid,
        zajmoprimac_naziv: c.naziv_firme || c.skraceni_naziv || '',
        zajmoprimac_identifikacija: c.pib || '',
      }));
    }
  };

  const handleSubmit = async () => {
    if (!form.client_id) { toast.error('Izaberite klijenta'); return; }
    if (!form.iznos) { toast.error('Unesite iznos'); return; }
    try {
      if (editingId) {
        await api.put(`/ugovori/${editingId}`, form);
        toast.success('Sačuvano');
      } else {
        await api.post('/ugovori', form);
        toast.success('Ugovor kreiran');
      }
      setShowForm(false); setEditingId(null); setForm(emptyForm); fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Greška'); }
  };

  const handleEdit = (e) => { setForm({ ...emptyForm, ...e }); setEditingId(e.id); setShowForm(true); };
  const handleDelete = async (id) => {
    if (!(await confirmDialog('Obrisati ugovor?'))) return;
    try { await api.delete(`/ugovori/${id}`); toast.success('Obrisano'); fetchAll(); } catch { toast.error('Greška'); }
  };

  const handlePrint = (entry) => {
    const client = clients.find(c => c.id === entry.client_id);
    let html;
    if (entry.tip_ugovora === 'racunovodstvo') html = generateUgovorRacunovodstvo(entry, client, agency);
    else if (entry.tip_ugovora === 'zajam') html = generateUgovorZajam(entry, client);
    else if (entry.tip_ugovora === 'zakup') html = generateUgovorZakup(entry, client);
    else if (entry.tip_ugovora === 'djelo') html = generateUgovorDjelo(entry, client);
    else { toast.info('Šablon nije implementiran'); return; }
    printHTML(html);
  };

  const handleWord = (entry) => {
    const client = clients.find(c => c.id === entry.client_id);
    let html;
    if (entry.tip_ugovora === 'racunovodstvo') html = generateUgovorRacunovodstvo(entry, client, agency);
    else if (entry.tip_ugovora === 'zajam') html = generateUgovorZajam(entry, client);
    else if (entry.tip_ugovora === 'zakup') html = generateUgovorZakup(entry, client);
    else if (entry.tip_ugovora === 'djelo') html = generateUgovorDjelo(entry, client);
    else { toast.info('Šablon nije implementiran'); return; }
    const name = `${getTipLabel(entry.tip_ugovora)}_${(entry.zajmoprimac_naziv || '').replace(/\s+/g, '_')}.docx`;
    downloadWord(html, name);
  };

  if (loading) return <div className="p-8 text-center text-slate-500">Učitavanje...</div>;

  return (
    <div className="space-y-6" data-testid="ugovori-page">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium text-slate-900">Ugovori</h3>
          <p className="text-sm text-slate-600 mt-1">Zajam, zakup, djelo i ostali ugovori</p>
        </div>
        <Button onClick={() => { setForm(emptyForm); setEditingId(null); setShowForm(true); }}
          className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="ugovori-add-btn">
          <Plus size={16} className="mr-2" /> Novi ugovor
        </Button>
      </div>

      <Card className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Pretraži..." className="pl-9 bg-white border-slate-300 text-sm" />
          </div>
          <select value={filterClient} onChange={e => setFilterClient(e.target.value)}
            className="flex w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm h-10">
            <option value="all">Svi klijenti</option>
            {sortedClients.map(c => <option key={c.id} value={c.id}>{c.skraceni_naziv || c.naziv_firme}</option>)}
          </select>
          <select value={filterTip} onChange={e => setFilterTip(e.target.value)}
            className="flex w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm h-10">
            <option value="all">Svi tipovi</option>
            {TIP_UGOVORA.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          <p className="text-sm text-slate-500 flex items-center">{filtered.length} zapisa</p>
        </div>
      </Card>

      {filtered.length === 0 ? (
        <Card className="p-12 text-center">
          <FileText size={48} className="mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500">Nema ugovora</p>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <table className="w-full text-left text-sm text-slate-600">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Klijent</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Tip</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Zajmodavac</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Zajmoprimac</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Iznos</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Datum</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Akcije</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(e => (
                <tr key={e.id} className="border-b hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium">{getClientName(e.client_id)}</td>
                  <td className="px-4 py-3 text-xs"><span className="px-2 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200">{getTipLabel(e.tip_ugovora)}</span></td>
                  <td className="px-4 py-3">{e.zajmodavac_naziv}</td>
                  <td className="px-4 py-3">{e.zajmoprimac_naziv}</td>
                  <td className="px-4 py-3 font-mono">{e.iznos ? `${e.iznos} €` : '-'}</td>
                  <td className="px-4 py-3 text-xs">{formatDatePrint(e.datum_ugovora) || '-'}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      <button onClick={() => setShowDetail(e)} className="p-2 hover:bg-blue-100 rounded" title="Detalji"><Eye size={16} className="text-blue-600" /></button>
                      <button onClick={() => handlePrint(e)} className="p-2 hover:bg-green-100 rounded" title="Štampaj"><Printer size={16} className="text-green-600" /></button>
                      <button onClick={() => handleWord(e)} className="p-2 hover:bg-indigo-100 rounded" title="Preuzmi Word"><FileDoc size={16} className="text-indigo-600" /></button>
                      <button onClick={() => handleEdit(e)} className="p-2 hover:bg-amber-100 rounded" title="Izmijeni"><Pencil size={16} className="text-amber-600" /></button>
                      <button onClick={() => handleDelete(e.id)} className="p-2 hover:bg-red-100 rounded" title="Obriši"><Trash size={16} className="text-red-600" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* Form */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingId ? 'Izmijeni ugovor' : 'Novi ugovor'}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs text-slate-500 font-semibold">Klijent *</Label>
                <select value={form.client_id} onChange={e => handleClientSelect(e.target.value)}
                  className="flex w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm h-10">
                  <option value="">-- Izaberite --</option>
                  {sortedClients.map(c => <option key={c.id} value={c.id}>{c.skraceni_naziv || c.naziv_firme} ({c.pib})</option>)}
                </select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-500 font-semibold">Tip ugovora *</Label>
                <select value={form.tip_ugovora} onChange={e => setForm({...form, tip_ugovora: e.target.value})}
                  className="flex w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm h-10">
                  {TIP_UGOVORA.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
            </div>

            {form.tip_ugovora !== 'racunovodstvo' && (<>
            <h4 className="text-sm font-bold text-slate-700 border-b pb-1 text-center">{form.tip_ugovora === 'zakup' ? 'Zakupodavac' : form.tip_ugovora === 'djelo' ? 'Izvršilac (fizičko lice)' : 'Zajmodavac'}</h4>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1 relative"><Label className="text-xs text-slate-500">Naziv / Ime i prezime * (pretraga fizičkih lica)</Label>
                <Input value={flSearch || form.zajmodavac_naziv}
                  onChange={e => handleFlSearch(e.target.value)}
                  onFocus={() => { if (flResults.length > 0) setShowFlDropdown(true); }}
                  onBlur={() => setTimeout(() => setShowFlDropdown(false), 200)}
                  placeholder="Kucajte ime, prezime ili JMB..." className="bg-white border-slate-300 text-sm" autoComplete="off" />
                {showFlDropdown && flResults.length > 0 && (
                  <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-48 overflow-y-auto">
                    {flResults.map(fl => (
                      <button key={fl.id} type="button" onMouseDown={e => { e.preventDefault(); handleFlSelect(fl); }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 border-b border-slate-100 last:border-0">
                        <span className="font-medium">{fl.prezime} {fl.ime}</span>
                        <span className="text-xs text-slate-400 ml-2">JMB: {fl.jmb || '-'}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="space-y-1"><Label className="text-xs text-slate-500">JMB / Pasoš / PIB</Label>
                <Input value={form.zajmodavac_identifikacija} onChange={e => setForm({...form, zajmodavac_identifikacija: e.target.value})} placeholder="Auto iz fizičkih lica" className="bg-white border-slate-300 text-sm" /></div>
            </div>

            <h4 className="text-sm font-bold text-slate-700 border-b pb-1 text-center">{form.tip_ugovora === 'zakup' ? 'Zakupoprimac' : form.tip_ugovora === 'djelo' ? 'Naručilac (klijent)' : 'Zajmoprimac'}</h4>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-xs text-slate-500">Naziv firme *</Label>
                <Input value={form.zajmoprimac_naziv} onChange={e => setForm({...form, zajmoprimac_naziv: e.target.value})} placeholder="Auto-popunjeno iz klijenta" className="bg-white border-slate-300 text-sm" /></div>
              <div className="space-y-1"><Label className="text-xs text-slate-500">PIB</Label>
                <Input value={form.zajmoprimac_identifikacija} onChange={e => setForm({...form, zajmoprimac_identifikacija: e.target.value})} placeholder="PIB" className="bg-white border-slate-300 text-sm" /></div>
            </div>
            </>)}

            <h4 className="text-sm font-bold text-slate-700 border-b pb-1 text-center">{form.tip_ugovora === 'racunovodstvo' ? 'Mjesečna naknada i datum' : 'Uslovi'}</h4>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-xs text-slate-500">{form.tip_ugovora === 'racunovodstvo' ? 'Mjesečna naknada (EUR) *' : 'Iznos (EUR) *'}</Label>
                <Input value={form.iznos} onChange={e => {
                  const val = e.target.value;
                  setForm(prev => ({ ...prev, iznos: val, iznos_slovima: brojSlovima(val) }));
                }} placeholder="0,00" className="bg-white border-slate-300 text-sm" /></div>
              {form.tip_ugovora !== 'racunovodstvo' && (
                <div className="space-y-1"><Label className="text-xs text-slate-500">Iznos slovima (auto)</Label>
                  <Input value={form.iznos_slovima} onChange={e => setForm({...form, iznos_slovima: e.target.value})} className="bg-slate-50 border-slate-300 text-sm text-slate-600" /></div>
              )}
            </div>
            <div className="space-y-1"><Label className="text-xs text-slate-500">Datum ugovora</Label>
              <DateInput value={form.datum_ugovora} onChange={e => setForm({...form, datum_ugovora: e.target.value})} className="border-slate-300 text-sm" /></div>

            {form.tip_ugovora === 'zajam' && (<>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label className="text-xs text-slate-500">Kamata (% godišnje)</Label>
                  <Input value={form.kamata} onChange={e => setForm({...form, kamata: e.target.value})} placeholder="2" className="bg-white border-slate-300 text-sm" /></div>
                <div className="space-y-1"><Label className="text-xs text-slate-500">Rok otplate (godina)</Label>
                  <Input value={form.rok_otplate} onChange={e => setForm({...form, rok_otplate: e.target.value})} placeholder="5" className="bg-white border-slate-300 text-sm" /></div>
              </div>
              <div className="space-y-1"><Label className="text-xs text-slate-500">Namjena zajma</Label>
                <Input value={form.namjena} onChange={e => setForm({...form, namjena: e.target.value})} placeholder="plaćanje tekućih obaveza" className="bg-white border-slate-300 text-sm" /></div>
            </>)}

            {form.tip_ugovora === 'zakup' && (<>
              <h4 className="text-sm font-bold text-slate-700 border-b pb-1 text-center">Detalji zakupa</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label className="text-xs text-slate-500">Adresa objekta *</Label>
                  <Input value={form.zakup_adresa} onChange={e => setForm({...form, zakup_adresa: e.target.value})} placeholder="npr: RASTIŠ" className="bg-white border-slate-300 text-sm" /></div>
                <div className="space-y-1"><Label className="text-xs text-slate-500">Površina (m²)</Label>
                  <Input value={form.zakup_povrsina} onChange={e => setForm({...form, zakup_povrsina: e.target.value})} placeholder="npr: 50m²" className="bg-white border-slate-300 text-sm" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label className="text-xs text-slate-500">Katastarska parcela</Label>
                  <Input value={form.zakup_kat_parcela} onChange={e => setForm({...form, zakup_kat_parcela: e.target.value})} placeholder="npr: 1211/1" className="bg-white border-slate-300 text-sm" /></div>
                <div className="space-y-1"><Label className="text-xs text-slate-500">List nepokretnosti br.</Label>
                  <Input value={form.zakup_list_nepokretnosti} onChange={e => setForm({...form, zakup_list_nepokretnosti: e.target.value})} placeholder="npr: 29" className="bg-white border-slate-300 text-sm" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label className="text-xs text-slate-500">Period od *</Label>
                  <DateInput value={form.zakup_period_od} onChange={e => setForm({...form, zakup_period_od: e.target.value})} className="border-slate-300 text-sm" /></div>
                <div className="space-y-1"><Label className="text-xs text-slate-500">Period do *</Label>
                  <DateInput value={form.zakup_period_do} onChange={e => setForm({...form, zakup_period_do: e.target.value})} className="border-slate-300 text-sm" /></div>
              </div>
              <div className="space-y-1"><Label className="text-xs text-slate-500">Namjena prostora</Label>
                <Input value={form.zakup_namjena} onChange={e => setForm({...form, zakup_namjena: e.target.value})} placeholder="skladištenje robe u magacinu" className="bg-white border-slate-300 text-sm" /></div>
              <div className="space-y-1"><Label className="text-xs text-slate-500">Troškovi (struja, voda...)</Label>
                <Input value={form.zakup_troskovi} onChange={e => setForm({...form, zakup_troskovi: e.target.value})} placeholder="Zakupodavac je obavezan plaćati troškove struje i vode" className="bg-white border-slate-300 text-sm" /></div>
              <div className="space-y-1"><Label className="text-xs text-slate-500">Grad</Label>
                <Input value={form.zakup_grad} onChange={e => setForm({...form, zakup_grad: e.target.value})} className="bg-white border-slate-300 text-sm" /></div>
            </>)}

            {form.tip_ugovora === 'djelo' && (<>
              <h4 className="text-sm font-bold text-slate-700 border-b pb-1 text-center">Detalji ugovora o djelu</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label className="text-xs text-slate-500">Grad zaključenja ugovora *</Label>
                  <Input value={form.djelo_grad} onChange={e => setForm({...form, djelo_grad: e.target.value})} placeholder="Ulcinj" className="bg-white border-slate-300 text-sm" /></div>
                <div className="space-y-1"><Label className="text-xs text-slate-500">Grad Izvršioca (prebivalište)</Label>
                  <Input value={form.djelo_izvrsilac_grad} onChange={e => setForm({...form, djelo_izvrsilac_grad: e.target.value})} placeholder="isto kao grad ugovora" className="bg-white border-slate-300 text-sm" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label className="text-xs text-slate-500">Ovlašćeno lice Naručioca</Label>
                  <Input value={form.djelo_narucilac_ovlasceno} onChange={e => setForm({...form, djelo_narucilac_ovlasceno: e.target.value})} placeholder="auto iz direktora klijenta" className="bg-white border-slate-300 text-sm" /></div>
                <div className="space-y-1"><Label className="text-xs text-slate-500">Ovlašćeno lice Izvršioca</Label>
                  <Input value={form.djelo_izvrsilac_ovlasceno} onChange={e => setForm({...form, djelo_izvrsilac_ovlasceno: e.target.value})} placeholder="auto ime Izvršioca" className="bg-white border-slate-300 text-sm" /></div>
              </div>
              <div className="space-y-1"><Label className="text-xs text-slate-500">Opis izvedenih radova *</Label>
                <textarea value={form.djelo_radovi} onChange={e => setForm({...form, djelo_radovi: e.target.value})}
                  placeholder="npr: krečenje poslovnih prostorija, montaža regala..."
                  className="flex w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm min-h-[60px] resize-y" /></div>
            </>)}

            <div className="space-y-1"><Label className="text-xs text-slate-500">Napomena</Label>
              <textarea value={form.napomena} onChange={e => setForm({...form, napomena: e.target.value})}
                className="flex w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm min-h-[50px] resize-y" /></div>

            <div className="flex gap-3 justify-end pt-2 border-t">
              <Button variant="outline" onClick={() => setShowForm(false)}>Zatvori</Button>
              <Button onClick={handleSubmit} className="bg-blue-600 hover:bg-blue-700 text-white">Sačuvaj</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Detail */}
      <Dialog open={!!showDetail} onOpenChange={() => setShowDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {showDetail && (
            <>
              <DialogHeader><DialogTitle>{getTipLabel(showDetail.tip_ugovora)} - {getClientName(showDetail.client_id)}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3 mt-3 text-sm">
                {[
                  ['Klijent', getClientName(showDetail.client_id)],
                  ['Tip', getTipLabel(showDetail.tip_ugovora)],
                  ['Zajmodavac', showDetail.zajmodavac_naziv],
                  ['Zajmodavac ID', showDetail.zajmodavac_identifikacija],
                  ['Zajmoprimac', showDetail.zajmoprimac_naziv],
                  ['Zajmoprimac ID', showDetail.zajmoprimac_identifikacija],
                  ['Iznos', showDetail.iznos ? `${showDetail.iznos} €` : '-'],
                  ['Iznos slovima', showDetail.iznos_slovima],
                  ['Kamata', showDetail.kamata ? `${showDetail.kamata}%` : '-'],
                  ['Rok otplate', showDetail.rok_otplate ? `${showDetail.rok_otplate} god.` : '-'],
                  ['Namjena', showDetail.namjena],
                  ['Datum', formatDatePrint(showDetail.datum_ugovora)],
                ].map(([l,v]) => (
                  <div key={l} className="p-2 bg-slate-50 rounded border"><span className="text-xs text-slate-500">{l}</span><p className="font-medium">{v || '-'}</p></div>
                ))}
              </div>
              <div className="flex gap-2 mt-4 pt-3 border-t">
                <Button size="sm" onClick={() => handlePrint(showDetail)} className="bg-green-600 hover:bg-green-700 text-white text-xs">
                  <Printer size={14} className="mr-1" /> Štampaj
                </Button>
                <Button size="sm" onClick={() => handleWord(showDetail)} className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs">
                  <FileDoc size={14} className="mr-1" /> Word
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
